import { StrictMode } from 'react';

import * as Sentry from '@sentry/react';
import {
  createRouter,
  ErrorComponent,
  RouterProvider,
} from '@tanstack/react-router';
import { Amplify } from 'aws-amplify';
import ReactDOM from 'react-dom/client';

import { amplifyConfig } from '@aws/config/index.ts';

import { errorFallback } from './integrations/sentry/error-fallback.tsx';
import { initSentry } from './integrations/sentry/init.ts';
import { SentryTestTrigger } from './integrations/sentry/sentry-test-trigger.tsx';
import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx';
import reportWebVitals from './reportWebVitals.ts';
// Import the generated route tree
import { routeTree } from './routeTree.gen';

import './styles.css';

initSentry();

Amplify.configure(amplifyConfig);

// Create a new router instance

const { queryClient } = TanStackQueryProvider.getContext();
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: {
      isAuthenticated: false,
    },
  },
  defaultPreload: false,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  // Needed so Match installs CatchBoundary and defaultOnCatch runs.
  defaultErrorComponent: ErrorComponent,
  defaultOnCatch: (error, errorInfo) =>
    Sentry.captureReactException(error, errorInfo),
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById('app');
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={errorFallback}>
        <TanStackQueryProvider.Provider queryClient={queryClient}>
          <RouterProvider router={router} />
        </TanStackQueryProvider.Provider>
        <SentryTestTrigger />
      </Sentry.ErrorBoundary>
    </StrictMode>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
